/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { notification, Spin } from "antd";

import { getAuthData } from "../../helpers/authStorage";
import { getQueryParam } from "../../helpers/general";
import { actions as driverActions } from "../../redux/resources/driver";
import RegistrationSteps from "../../components/DataEntry/RegistrationSteps";
import PersonalInformationForm from "./components/PersonalInformationForm";
import UploadDocumentsForm from "./components/UploadDocumentsForm";
import YourCarForm from "./components/YourCarForm";
import "./style.less";

const DriverRegistration = ({
  location,
  confirmEmailDriver,
  putSelfProfileDriver,
  putSelfCarDriver,
  history,
  driverInfo,
  getSelfDriver,
  personalInfo,
  carInfo,
}) => {
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [currentStep, setCurrentStep] = useState(
    JSON.parse(localStorage.getItem("DriverRegistrationStep")) || 0
  );
  const [uploadedImage, setUploadedImage] = useState({
    nationalId: "",
    drivingLicense: "",
    policeClearanceCertificate: "",
    carRegistrationDocuments: "",
  });

  const [currentDriverPhone, setCurrentDriverPhone] = useState("");
  const [tokenUpdated, setTokenUpdated] = useState(false);
  
  // ✅ NEW: Token validation states
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [tokenValidationError, setTokenValidationError] = useState(null);
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [confirmationToken, setConfirmationToken] = useState(null);

  // ========================================
  // EFFECT 1: VALIDATE TOKEN ON PAGE LOAD
  // ========================================
  useEffect(() => {
    const queryParams = getQueryParam(location.search);

    // If no token in URL, skip validation
    if (!queryParams.confirmationToken) {
      return;
    }

    console.log("🔑 Token found:", queryParams.confirmationToken);
    setConfirmationToken(queryParams.confirmationToken);

    // ✅ Validate token WITHOUT consuming it
    const validateToken = async () => {
      try {
        setIsValidatingToken(true);
        setTokenValidationError(null);

        const apiUrl = process.env.REACT_APP_API_URL;
        const validateUrl = `${apiUrl}/auth/drivers/registration/validate-token?confirmationToken=${queryParams.confirmationToken}`;

        console.log("🔄 Validating token...");
        const response = await fetch(validateUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (!data.valid) {
          // ❌ Token is invalid or expired
          console.log("❌ Token validation failed:", data.message);
          const errorMsg =
            data.message || "Your registration link has expired. Please sign up again.";
          
          setTokenValidationError(errorMsg);
          setTokenIsValid(false);

          notification.error({
            message: "Registration Link Expired",
            description: errorMsg,
          });

          setTimeout(() => {
            history.push("/driver_signup");
          }, 3000);

          return;
        }

        // ✅ Token is valid - show form
        console.log("✅ Token is valid");
        setTokenIsValid(true);
        setIsValidatingToken(false);
      } catch (error) {
        console.error("❌ Token validation error:", error);
        setTokenValidationError("Unable to validate registration link");
        setTokenIsValid(false);

        notification.error({
          message: "Validation Error",
          description: "Unable to validate your registration link. Please try again.",
        });

        setTimeout(() => {
          history.push("/driver_signup");
        }, 3000);
      }
    };

    validateToken();
  }, [history, location.search]);

  // ========================================
  // EFFECT 2: AFTER SUCCESSFUL VALIDATION
  // ========================================
  useEffect(() => {
    const token = getAuthData().accessToken;

    if (token && tokenUpdated) {
      getSelfDriver();
    }
    
    localStorage.setItem("DriverRegistrationStep", currentStep.toString());
  }, [currentStep, getSelfDriver, tokenUpdated]);

  // ========================================
  // EFFECT 3: CLEANUP
  // ========================================
  useEffect(() => {
    return () => {
      localStorage.removeItem("driverPersonalInfo");
      localStorage.removeItem("driverCarInfo");
      localStorage.removeItem("DriverRegistrationStep");
    };
  }, []);

  // ========================================
  // HANDLERS
  // ========================================
  const onNextStep = () => {
    if (
      currentStep === 0 &&
      personalInfo?.phoneNumber &&
      (personalInfo?.phoneNumber === currentDriverPhone || !currentDriverPhone)
    ) {
      const current = currentStep + 2;
      setCurrentStep(current);
    } else {
      const current = currentStep + 1;
      setCurrentStep(current);
    }
  };

  const onPrevStep = () => {
    if (
      currentStep === 2 &&
      personalInfo?.phoneNumber &&
      (personalInfo?.phoneNumber === currentDriverPhone || !currentDriverPhone)
    ) {
      const current = currentStep - 2;
      setCurrentStep(current);
    } else {
      const current = currentStep - 1;
      setCurrentStep(current);
    }
  };

  const openErrorNotification = (error) => {
    notification.error({
      message: "Error",
      description: error.body && error.body.message && error.body.message,
    });
  };

  const areValuesChanged = (values) => {
    const currentValuesFields = Object.keys(values);
    const changedValue = currentValuesFields.find(
      (currentValueField) =>
        driverInfo[currentValueField] !== values[currentValueField]
    );
    return changedValue;
  };

  const putSelfProfileDriverWithError = (values) => {
    const changedFieldName = areValuesChanged(values);
    return changedFieldName
      ? putSelfProfileDriver(values)
          .then(() => {
            onNextStep();
          })
          .catch((error) => {
            openErrorNotification(error);
          })
      : onNextStep();
  };

  const putSelfCarDriverWithError = (values) => {
    const changedFieldName = areValuesChanged(values);
    return changedFieldName
      ? putSelfCarDriver(values)
          .then(() => {
            onNextStep();
          })
          .catch((error) => {
            openErrorNotification(error);
          })
      : onNextStep();
  };

  // ========================================
  // RENDER: LOADING STATE
  // ========================================
  if (isValidatingToken) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <h2 style={{ marginTop: "20px" }}>Validating your registration link...</h2>
      </div>
    );
  }

  // ========================================
  // RENDER: ERROR STATE
  // ========================================
  if (tokenValidationError) {
    return (
      <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
        <h2>❌ {tokenValidationError}</h2>
      </div>
    );
  }

  // ========================================
  // RENDER: CONFIRMATION TOKEN BUT NOT VALIDATED
  // ========================================
  if (confirmationToken && !tokenIsValid && !isValidatingToken) {
    return (
      <div style={{ textAlign: "center", padding: "50px", color: "orange" }}>
        <h2>⚠️ Token validation failed</h2>
      </div>
    );
  }

  // ========================================
  // RENDER: NORMAL REGISTRATION STEPS
  // ========================================
  
  const steps = [
    {
      title: (
        <span className="stepTitleDisplaying">
          <FormattedMessage id="page.step.driverInfo" defaultMessage="Driver Info" />
        </span>
      ),
      additionalTitle: (
        <span className="stepAdditionalTitle">
          <FormattedMessage id="page.step.driverInfo" defaultMessage="Driver Info" />
        </span>
      ),
      details: "Driver Info",
      content: (
        <PersonalInformationForm
          personalInfo={personalInfo}
          driverInfo={driverInfo}
          putSelfProfileDriverWithError={putSelfProfileDriverWithError}
          currentStep={currentStep}
          onPrevStep={onPrevStep}
          stepsLength={6}
          setCurrentDriverPhone={setCurrentDriverPhone}
        />
      ),
    },
    {
      title: (
        <span className="stepTitleDisplaying">
          <FormattedMessage id="page.step.carInfo" defaultMessage="Car Info" />
        </span>
      ),
      additionalTitle: (
        <span className="stepAdditionalTitle">
          <FormattedMessage id="page.step.carInfo" defaultMessage="Car Info" />
        </span>
      ),
      details: "Car Info",
      content: (
        <YourCarForm
          carInfo={carInfo}
          driverInfo={driverInfo}
          onNextStep={onNextStep}
          putSelfCarDriver={putSelfCarDriverWithError}
          currentStep={currentStep}
          onPrevStep={onPrevStep}
          stepsLength={6}
        />
      ),
    },
    {
      title: (
        <span className="stepTitleDisplaying">
          <FormattedMessage
            id="page.step.driver.documentUpload"
            defaultMessage="Document Upload"
          />
        </span>
      ),
      additionalTitle: (
        <span className="stepAdditionalTitle">
          <FormattedMessage
            id="page.step.driver.documentUpload"
            defaultMessage="Document Upload"
          />
        </span>
      ),
      details: "DocumensList Upload",
      content: (
        <UploadDocumentsForm
          driverInfo={driverInfo}
          onPrevStep={onPrevStep}
          uploadedImage={uploadedImage}
          setUploadedImage={setUploadedImage}
          onNextStep={onNextStep}
        />
      ),
    },
  ];

  return (
    <div>
      <RegistrationSteps
        steps={steps}
        onNextStep={onNextStep}
        onPrevStep={onPrevStep}
        currentStep={currentStep}
      />
    </div>
  );
};

const MSTP = (state) => {
  return {
    driverInfo: state.driver.items,
    personalInfo: state.driver.selfInfo?.profile,
    carInfo: state.driver.selfInfo?.car,
  };
};

export default connect(MSTP, (dispatch) => ({
  ...bindActionCreators(driverActions, dispatch),
}))(DriverRegistration);
